import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Radar, Lock } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1651505942687-efc26cb528ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHJhZGFyJTIwZGF0YSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg3MTIxODUzfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Signed in");
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toast.error(err.message || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: brand / imagery */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1C3D]/97 via-[#0B1C3D]/94 to-[#0B1C3D]/98" />
        <div className="absolute inset-0 [background:radial-gradient(120%_120%_at_20%_20%,transparent,rgba(11,28,61,0.55))]" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-white/10 flex items-center justify-center">
            <Radar className="h-6 w-6 text-teal-400" />
          </div>
          <div>
            <div className="font-heading font-bold text-lg">DefencIQ Radar</div>
            <div className="text-xs uppercase tracking-widest text-teal-300/80">Defence-market intelligence</div>
          </div>
        </div>
        <div className="relative z-10 max-w-md [text-shadow:0_2px_16px_rgba(0,0,0,0.75)]">
          <h1 className="font-heading text-3xl font-bold leading-tight text-white">Evidence-led qualification of emerging Australian Defence SMEs.</h1>
          <p className="mt-4 text-slate-50 text-sm leading-relaxed">
            An opportunity and readiness triage tool — not an eligibility, certification or approval system.
            Discovery Signals and Qualification Evidence are kept distinct, and inferences are never presented as verified facts.
          </p>
        </div>
        <div className="relative z-10 text-[11px] text-slate-400">DefencIQ Pty Ltd — internal unclassified system</div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 bg-slate-50">
        <form onSubmit={submit} className="w-full max-w-sm bg-white border border-slate-200 rounded-lg p-8" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <div className="h-9 w-9 rounded-md bg-[#0B1C3D] flex items-center justify-center"><Radar className="h-5 w-5 text-teal-400" /></div>
            <span className="font-heading font-bold text-slate-900">DefencIQ Radar</span>
          </div>
          <h2 className="font-heading text-xl font-bold text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1">Authenticated internal access only.</p>

          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@defenciq.local" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1" />
            </div>
          </div>

          <Button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full mt-6 bg-[#0B1C3D] hover:bg-[#122a55]">
            <Lock className="h-4 w-4 mr-2" /> {loading ? "Signing in…" : "Sign in"}
          </Button>

          <div className="mt-6 rounded-md bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-500 leading-relaxed">
            <div className="font-semibold text-slate-600 mb-1">Demo accounts</div>
            admin@defenciq.local · analyst@defenciq.local · sales@defenciq.local · viewer@defenciq.local
          </div>
        </form>
      </div>
    </div>
  );
}
